import React, { useEffect, useMemo, useState } from 'react';
import { resolvePreviewKind, resolvePreviewMime } from './assetUtils';

export default function SelectedAssetPreview({ asset, className, maxHeight = 320 }) {
  const [state, setState] = useState({ url: null, contentType: null, loading: false, error: null });
  const assetId = asset?.id || null;
  const previewKind = useMemo(() => resolvePreviewKind(asset), [asset]);

  useEffect(() => {
    let controller = null;
    let objectUrl = null;

    if (!assetId) {
      setState({ url: null, contentType: null, loading: false, error: null });
      return () => {};
    }

    const token = localStorage.getItem('token');
    controller = new AbortController();
    setState({ url: null, contentType: null, loading: true, error: null });

    async function loadPreview() {
      try {
        const res = await fetch(
          `/api/creative/assets/${assetId}/files/${encodeURIComponent(previewKind)}`,
          {
            cache: 'no-store',
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );
        if (!res.ok) throw new Error(`Failed to load asset preview (${res.status})`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        const contentType = res.headers.get('Content-Type') || resolvePreviewMime(asset, previewKind) || '';
        setState({ url: objectUrl, contentType, loading: false, error: null });
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Failed to open asset preview', err);
        setState({ url: null, contentType: null, loading: false, error: 'Unable to load preview.' });
      }
    }

    loadPreview();

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [assetId, previewKind]);

  const isImage = state.contentType ? state.contentType.startsWith('image/') : false;

  return (
    <div className={`bg-gray-900 text-white rounded-lg p-4 ${className || ''}`}>
      <h3 className='text-lg font-semibold mb-3'>Preview Window</h3>
      {!asset && <div className='text-sm text-gray-400'>Select a photo to preview.</div>}
      {asset && state.loading && <div className='text-sm text-gray-300'>Loading preview…</div>}
      {asset && state.error && <div className='text-sm text-red-400'>{state.error}</div>}
      {asset && !state.loading && !state.error && state.url && isImage && (
        <div className='border border-gray-800 rounded-md overflow-hidden bg-black'>
          <img
            src={state.url}
            alt='Selected asset preview'
            className='w-full h-full object-contain'
            style={{ maxHeight }}
          />
        </div>
      )}
      {asset && !state.loading && !state.error && state.url && !isImage && (
        <div className='text-sm text-gray-400'>Preview available for download only.</div>
      )}
    </div>
  );
}
